// @flow
import { of } from 'rxjs';
import { mergeMap, map, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import update from 'immutability-helper';
import { fetchSessionApi, postResendVerificationApi, postSendForgotPasswordApi, postRecoverUserApi } from './api';
import { loginSuccess } from '../duck';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../../error/index';
import type { UserMini } from '../../../entities/index';

type AuthModalsAction = {
  +type: string;
  +error?: HTTPError;
  +email?: string;
  +password?: string;
  +token?: string;
};

export type AuthModalsState = {
  +isVerifying?: boolean;
  +verificationError?: HTTPError;
  +isResending?: boolean;
  +resendError?: HTTPError;
  +verificationSuccess?: boolean;
  // forgot-password
  +isSendingForgotPassword?: boolean;
  +sendForgotPasswordError?: HTTPError;
  +sendForgotPasswordSuccess?: boolean;
  // recover user
  +isRecoveringUser?: boolean;
  +recoverUserSuccess?: boolean;
  +recoverUserError?: HTTPError;
  +forgotPasswordToken?: string;
  +changePasswordModalVisible?: boolean;
};

// Actions
const AUTH_MODALS_RESET_STATE = 'AUTH_MODALS_RESET_STATE';

const AUTH_MODALS_VERIFICATION_FETCH_SESSION_REQUEST = 'AUTH_MODALS_VERIFICATION_FETCH_SESSION_REQUEST';
const AUTH_MODALS_VERIFICATION_FETCH_SESSION_SUCCESS = 'AUTH_MODALS_VERIFICATION_FETCH_SESSION_SUCCESS';
const AUTH_MODALS_VERIFICATION_FETCH_SESSION_ERROR = 'AUTH_MODALS_VERIFICATION_FETCH_SESSION_ERROR';

const AUTH_MODALS_VERIFICATION_POST_RESEND_REQUEST = 'AUTH_MODALS_VERIFICATION_POST_RESEND_REQUEST';
const AUTH_MODALS_VERIFICATION_POST_RESEND_SUCCESS = 'AUTH_MODALS_VERIFICATION_POST_RESEND_SUCCESS';
const AUTH_MODALS_VERIFICATION_POST_RESEND_ERROR = 'AUTH_MODALS_VERIFICATION_POST_RESEND_ERROR';

const AUTH_MODALS_SEND_FORGOT_PASSWORD_REQUEST = 'AUTH_MODALS_SEND_FORGOT_PASSWORD_REQUEST';
const AUTH_MODALS_SEND_FORGOT_PASSWORD_SUCCESS = 'AUTH_MODALS_SEND_FORGOT_PASSWORD_SUCCESS';
const AUTH_MODALS_SEND_FORGOT_PASSWORD_ERROR = 'AUTH_MODALS_SEND_FORGOT_PASSWORD_ERROR';

const AUTH_MODALS_RECOVER_USER_REQUEST = 'AUTH_MODALS_RECOVER_USER_REQUEST';
const AUTH_MODALS_RECOVER_USER_SUCCESS = 'AUTH_MODALS_RECOVER_USER_SUCCESS';
const AUTH_MODALS_RECOVER_USER_ERROR = 'AUTH_MODALS_RECOVER_USER_ERROR';


// Action creators
export function resetState(): AuthModalsState {
  return {
    type: AUTH_MODALS_RESET_STATE,
  };
}

export function getSession(): AuthModalsAction {
  return {
    type: AUTH_MODALS_VERIFICATION_FETCH_SESSION_REQUEST,
  }
}

export function getSessionSuccess(): AuthModalsAction {
  return {
    type: AUTH_MODALS_VERIFICATION_FETCH_SESSION_SUCCESS,
  }
}

export function getSessionError(error: HTTPError): AuthModalsAction {
  return {
    type: AUTH_MODALS_VERIFICATION_FETCH_SESSION_ERROR,
    error,
  }
}

export function resendVerification(): AuthModalsAction {
  return {
    type: AUTH_MODALS_VERIFICATION_POST_RESEND_REQUEST,
  }
}

export function resendVerificationSuccess(): AuthModalsAction {
  return {
    type: AUTH_MODALS_VERIFICATION_POST_RESEND_SUCCESS,
  }
}

export function resendVerificationError(error: HTTPError): AuthModalsAction {
  return {
    type: AUTH_MODALS_VERIFICATION_POST_RESEND_ERROR,
    error,
  }
}

export function sendForgotPassword(email: string): AuthModalsAction {
  return {
    type: AUTH_MODALS_SEND_FORGOT_PASSWORD_REQUEST,
    email,
  }
}

export function sendForgotPasswordSuccessFunc(): AuthModalsAction {
  return {
    type: AUTH_MODALS_SEND_FORGOT_PASSWORD_SUCCESS,
  }
}

export function sendForgotPasswordErrorFunc(error: HTTPError): AuthModalsAction {
  return {
    type: AUTH_MODALS_SEND_FORGOT_PASSWORD_ERROR,
    error,
  }
}

export function recoverUser(password: string, token: string): AuthModalsAction {
  return {
    type: AUTH_MODALS_RECOVER_USER_REQUEST,
    password,
    token,
  }
}

export function recoverUserSuccessFn(): AuthModalsAction {
  return {
    type: AUTH_MODALS_RECOVER_USER_SUCCESS,
  }
}

export function recoverUserErrorFn(error: HTTPError): AuthModalsAction {
  return {
    type: AUTH_MODALS_RECOVER_USER_ERROR,
    error,
  }
}

export const actions = {
  AUTH_MODALS_VERIFICATION_FETCH_SESSION_REQUEST,
  AUTH_MODALS_VERIFICATION_FETCH_SESSION_SUCCESS,
  AUTH_MODALS_VERIFICATION_FETCH_SESSION_ERROR,
  AUTH_MODALS_VERIFICATION_POST_RESEND_REQUEST,
  AUTH_MODALS_VERIFICATION_POST_RESEND_SUCCESS,
  AUTH_MODALS_VERIFICATION_POST_RESEND_ERROR,
  AUTH_MODALS_SEND_FORGOT_PASSWORD_REQUEST,
  AUTH_MODALS_SEND_FORGOT_PASSWORD_SUCCESS,
  AUTH_MODALS_SEND_FORGOT_PASSWORD_ERROR,
  AUTH_MODALS_RECOVER_USER_REQUEST,
  AUTH_MODALS_RECOVER_USER_SUCCESS,
  AUTH_MODALS_RECOVER_USER_ERROR,
  AUTH_MODALS_RESET_STATE,
  getSession,
  getSessionSuccess,
  getSessionError,
  resendVerification,
  resendVerificationSuccess,
  resendVerificationError,
  sendForgotPassword,
  sendForgotPasswordSuccessFunc,
  sendForgotPasswordErrorFunc,
  recoverUser,
  recoverUserSuccessFn,
  recoverUserErrorFn,
  resetState,
};

export const initialState: AuthModalsState = {};

// Reducer
export default function reducer(state: AuthModalsState = initialState,
                                action: AuthModalsAction): AuthModalsState {
  switch (action.type) {
    case AUTH_MODALS_VERIFICATION_FETCH_SESSION_REQUEST:
      return update(state, {
        $unset: ['verificationError'],
        isVerifying: { $set: true },
      });
    case AUTH_MODALS_VERIFICATION_FETCH_SESSION_SUCCESS:
      return update(state, {
        $unset: ['isVerifying'],
      });
    case AUTH_MODALS_VERIFICATION_FETCH_SESSION_ERROR:
      return update(state, {
        $unset: ['isVerifying'],
        verificationError: { $set: action.error },
      });
    case AUTH_MODALS_VERIFICATION_POST_RESEND_REQUEST:
      return update(state, {
        $unset: ['resendError'],
        isResending: { $set: true },
      });
    case AUTH_MODALS_VERIFICATION_POST_RESEND_SUCCESS:
      return update(state, {
        $unset: ['isResending'],
      });
    case AUTH_MODALS_VERIFICATION_POST_RESEND_ERROR:
      return update(state, {
        $unset: ['isResending'],
        resendError: { $set: action.error },
      });
    case AUTH_MODALS_SEND_FORGOT_PASSWORD_REQUEST:
      return update(state, {
        $unset: ['sendForgotPasswordError', 'sendForgotPasswordSuccess'],
        isSendingForgotPassword: { $set: true },
      });
    case AUTH_MODALS_SEND_FORGOT_PASSWORD_SUCCESS:
      return update(state, {
        $unset: ['isSendingForgotPassword'],
        sendForgotPasswordSuccess: { $set: true },
      });
    case AUTH_MODALS_SEND_FORGOT_PASSWORD_ERROR:
      return update(state, {
        $unset: ['isSendingForgotPassword', 'sendForgotPasswordSuccess'],
        sendForgotPasswordError: { $set: action.error },
      });
    case AUTH_MODALS_RECOVER_USER_REQUEST:
      return update(state, {
        $unset: ['recoverUserError'],
        isRecoveringUser: { $set: true },
      });
    case AUTH_MODALS_RECOVER_USER_SUCCESS:
      return update(state, {
        $unset: ['isRecoveringUser'],
        recoverUserSuccess: { $set: true },
      });
    case AUTH_MODALS_RECOVER_USER_ERROR:
      return update(state, {
        $unset: ['isRecoveringUser'],
        recoverUserError: { $set: action.error },
      });
    case AUTH_MODALS_RESET_STATE:
      return initialState;
    default:
      return state;
  }
}

// Epics
export function fetchSessionEpic(action$: Observable<AuthModalsAction>): Observable<AuthModalsAction> {
  return action$.pipe(
    ofType(AUTH_MODALS_VERIFICATION_FETCH_SESSION_REQUEST),
    mergeMap(() => fetchSessionApi().pipe(
      mergeMap((response: UserMini) => of(getSessionSuccess(), loginSuccess(response))),
      catchError((e: HTTPError) => of(getSessionError(e))))
    ));
}

export function resendVerificationEpic(action$: Observable<AuthModalsAction>): Observable<AuthModalsAction> {
  return action$.pipe(
    ofType(AUTH_MODALS_VERIFICATION_POST_RESEND_REQUEST),
    mergeMap(() => postResendVerificationApi().pipe(
      map(() => resendVerificationSuccess()),
      catchError((e: HTTPError) => of(resendVerificationError(e))))
    ));
}

export function sendForgotPasswordEpic(action$: Observable<AuthModalsAction>): Observable<AuthModalsAction> {
  return action$.pipe(
    ofType(AUTH_MODALS_SEND_FORGOT_PASSWORD_REQUEST),
    mergeMap(({ email }) => postSendForgotPasswordApi({ email }).pipe(
      map(() => sendForgotPasswordSuccessFunc()),
      catchError((e: HTTPError) => of(sendForgotPasswordErrorFunc(e))))
    ));
}

export function recoverUserEpic(action$: Observable<AuthModalsAction>): Observable<AuthModalsAction> {
  return action$.pipe(
    ofType(AUTH_MODALS_RECOVER_USER_REQUEST),
    mergeMap(({ password, token }) => postRecoverUserApi({ password, token }).pipe(
      map(() => recoverUserSuccessFn()),
      catchError((e: HTTPError) => of(recoverUserErrorFn(e))))
    ));
}



// WEBPACK FOOTER //
// ./src/modules/auth/auth-modals/duck.js